import { useCallback, useState } from 'react';

const SIDEBAR_COLLAPSED_KEY = 'borgee:shell:sidebarCollapsed';

// localStorage throws instead of returning null under private browsing and when
// the origin is out of quota. A layout preference is a convenience, so both
// accessors degrade to the expanded default rather than propagating.
const readCollapsed = (): boolean => {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeCollapsed = (collapsed: boolean): void => {
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  } catch {
    // Losing the preference only costs the member one click after a reload.
  }
};

export interface SidebarCollapsed {
  collapsed: boolean;
  toggle: () => void;
}

/**
 * Whether the member has collapsed the sidebar column away, remembered across
 * reloads. It is a stated preference rather than a transient view state, so it
 * is read once at mount and written on every toggle instead of being derived
 * from the current route or viewport.
 */
export function useSidebarCollapsed(): SidebarCollapsed {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggle = useCallback(() => {
    const next = !collapsed;
    writeCollapsed(next);
    setCollapsed(next);
  }, [collapsed]);

  return { collapsed, toggle };
}
